/**
 * @group Functions
 * 
 * @summary
 * <blockquote>Handles tenant UUID-related errors by showing appropriate toast notifications.</blockquote>
 * 
 * @example
 * ```typescript
 * const handled = handleTenantSignupError({
 *   errorCode: 'INVALID_TENANT_UUID',
 *   toast,
 *   setOnboardingBlockedMessage,
 * });
 * 
 * if (handled) {
 *   return; // Error was handled, exit early
 * }
 * ```
 */

import * as React from "react";
import Link from "next/link";
import { AUTH_MESSAGES } from '@shared/constants';

/**
 * Supported tenant UUID error codes that trigger the special error handling.
 */
const TENANT_UUID_ERROR_CODES = [
  'INVALID_TENANT_UUID',
  'TENANT_NOT_FOUND',
  'TENANT_UUID_TAMPERED',
] as const;

type TenantUuidErrorCode = typeof TENANT_UUID_ERROR_CODES[number];

/**
 * Checks if an error code is a tenant UUID error.
 */
function isTenantUuidError(errorCode: string | number | null | undefined): errorCode is TenantUuidErrorCode {
  return typeof errorCode === 'string' && TENANT_UUID_ERROR_CODES.includes(errorCode as TenantUuidErrorCode);
}

/**
 * Gets the appropriate error message for a tenant UUID error code.
 */
function getTenantUuidErrorMessage(errorCode: TenantUuidErrorCode): string {
  return errorCode === 'TENANT_UUID_TAMPERED'
    ? AUTH_MESSAGES.ERRORS.TENANT_UUID_TAMPERED
    : AUTH_MESSAGES.ERRORS.INVALID_TENANT_UUID;
}

/**
 * Parameters for handling tenant signup errors.
 */
interface HandleTenantSignupErrorParams {
  /**
   * The error code from the API response or error object.
   */
  errorCode: string | number | null | undefined;
  /**
   * Toast function to display notifications.
   */
  toast: (options: {
    title: string;
    description: string | React.ReactNode;
    variant: 'default' | 'destructive';
  }) => void;
  /**
   * Optional function to set onboarding blocked message state.
   */
  setOnboardingBlockedMessage?: (message: string | null) => void;
}

/**
 * Handles tenant UUID-related errors by showing a toast notification with a link to individual signup.
 * 
 * @param params - Parameters containing error code, toast function, and optional state setter
 * @returns `true` if the error was handled (was a tenant UUID error), `false` otherwise
 */
export function handleTenantSignupError({
  errorCode,
  toast,
  setOnboardingBlockedMessage,
}: HandleTenantSignupErrorParams): boolean {
  if (!isTenantUuidError(errorCode)) {
    return false;
  }

  const errorMessage = getTenantUuidErrorMessage(errorCode);

  // Set onboarding blocked message if setter is provided
  if (setOnboardingBlockedMessage) {
    setOnboardingBlockedMessage(errorMessage);
  }

  // Show toast notification
  toast({
    title: "Invalid signup link",
    description: (
      <div className="space-y-2">
        <p>{errorMessage}</p>
        <p className="text-sm">
          Otherwise, you can{' '}
          <Link href="/users/signup" className="underline underline-offset-4 hover:text-primary font-medium">
            sign up as an individual
          </Link>
          .
        </p>
      </div>
    ),
    variant: "destructive",
  });

  return true;
}
